<template>
  <div class="row">

        <div class="col-md-4 mb-4">
          <div class="card">
            <div class="card-header d-flex justify-content-between align-items-center bg-danger text-white">
              <div class="card-title mb-0">Использование диска</div>
              <div class="badge">
                {{ currentDiskUsage }} / {{ currentDiskTotal }} ({{ usagePercentage }}%)
              </div>
            </div>
            <div class="card-body">
              <div class="chart-container" style="position: relative; height: 200px;">
                <canvas ref="chartDisc"></canvas>
              </div>
            </div>
          </div>
        </div>

    <div class="col-md-4 mb-4">
      <div class="card h-100">
        <div class="card-header bg-success text-white">
          <div class="card-title mb-0">Оперативная память</div>
        </div>
        <div class="card-body">
          <canvas ref="chartMemory"></canvas>
        </div>
      </div>
    </div>

    <div class="col-md-4 mb-4">
      <h4>Статусы</h4>
      <table class="table table-striped table-sm">
        <thead>
        <tr>
          <th scope="col"></th>
        </tr>
        </thead>
        <tbody>
        <tr>
          <td>Название сервера:</td><td>{{server.name}}</td>
        </tr>
        <tr>
          <td>Хост сервера:</td><td>{{server.host}}</td>
        </tr>
        <tr>
          <td>Количество процессов:</td><td>{{currentProcesses}}</td>
        </tr>
        <tr>
          <td>Время непрерывной работы (сек):</td><td>{{currentUptime}}</td>
        </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import {ref, onMounted, onBeforeUnmount, defineProps, computed, watch} from 'vue'
import { Chart } from 'chart.js/auto'
import {useServersStore} from "@/store/servers";
import {useNotificationsStore} from "@/store/notifications";

const props  = defineProps({
  serverId: Number,
})

const serversStore = useServersStore()

const serverMetrics = computed(() => serversStore.metrics)
serversStore.getMetrics({serverId: props.serverId, scheduleInterval: 'minute'})

const server = computed(() => serversStore.item)
serversStore.getById(props.serverId)

const formatTime = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Байт', 'КБ', 'МБ', 'ГБ', 'ТБ'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const chartDisc = ref(null)
const chartMemory = ref(null)
let chartInstanceDisc = null
let chartInstanceMemory = null

// Берем последние данные
const currentStats = computed(() => {
  return serverMetrics.value.length > 0
      ? serverMetrics.value[serverMetrics.value.length - 1]
      : null;
});

const chartDiscData = computed(() => {
  if (!currentStats.value) return { datasets: [] };

  return {
    labels: ['Занято', 'Свободно'],
    datasets: [{
      data: [
        parseInt(currentStats.value.diskUsed),
        parseInt(currentStats.value.diskFree)
      ],
      backgroundColor: [
        'rgba(220, 53, 69, 1)',
        'rgba(220, 53, 69, 0.2)'
      ],
    }]
  };
});

const chartMemoryData = computed(() => {
  const sortedData = [...serverMetrics.value].sort((a, b) =>
      new Date(a.updatedAt) - new Date(b.updatedAt)
  );

  return {
    labels: sortedData.map(item => formatTime(item.updatedAt)),
    datasets: [
      {
        label: 'Занято',
        data: sortedData.map(item => parseInt(item.memoryUsed)),
        backgroundColor: 'rgba(25, 135, 84, 0.7)',
      },
    ],
    maxTotal: Math.max(...sortedData.map(item => parseInt(item.memoryTotal)))
  };
});

const currentProcesses = computed(() => {
  return currentStats.value
      ? parseInt(currentStats.value.processes)
      : '0';
});

const currentUptime = computed(() => {
  return currentStats.value
      ? parseInt(currentStats.value.uptime)
      : '0';
});

const currentDiskTotal = computed(() => {
  return currentStats.value
      ? formatBytes(parseInt(currentStats.value.diskTotal))
      : '0';
});

const currentDiskUsage = computed(() => {
  return currentStats.value
      ? formatBytes(parseInt(currentStats.value.diskUsed))
      : '0';
});

const usagePercentage = computed(() => {
  if (!currentStats.value) return 0;
  const total = parseInt(currentStats.value.diskTotal);
  const used = parseInt(currentStats.value.diskUsed);
  return total > 0 ? ((used / total) * 100).toFixed(1) : 0;
});

const initChart = () => {
  if (!currentStats.value) return;

  chartInstanceDisc = new Chart(chartDisc.value.getContext('2d'), {
    type: 'doughnut',
    data: chartDiscData.value,
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const label = context.label || '';
              const value = context.raw;
              const percent = context.dataset.data.reduce((a, b) => a + b, 0) > 0
                  ? ((value / context.dataset.data.reduce((a, b) => a + b, 0)) * 100).toFixed(1)
                  : 0;
              return `${label}: ${formatBytes(value)} (${percent}%)`;
            }
          }
        }
      },
      animation: {
        animateScale: true,
        animateRotate: true
      }
    }
  });

  chartInstanceMemory = new Chart(chartMemory.value.getContext('2d'), {
    type: 'bar',
    data: {
      labels: chartMemoryData.value.labels,
      datasets: chartMemoryData.value.datasets
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'x',
      scales: {
        x: {
          stacked: true,
          ticks: {
            maxRotation: 45,
            minRotation: 45
          }
        },
        y: {
          stacked: true,
          max: chartMemoryData.value.maxTotal,
          ticks: {
            callback: formatBytes
          }
        }
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: (context) => {
              const label = context.dataset.label || '';
              const value = context.raw;
              const percent = ((value / chartMemoryData.value.maxTotal) * 100).toFixed(1);
              return `${label}: ${formatBytes(value)} (${percent}%)`;
            }
          }
        },
        legend: {
          position: 'top'
        }
      }
    }
  });
};

const updateChart = () => {
  chartInstanceDisc.data = chartDiscData.value
  chartInstanceMemory.data.labels = chartMemoryData.value.labels
  chartInstanceMemory.data.datasets = chartMemoryData.value.datasets
  chartInstanceMemory.update()
}

onMounted(async () => {
  await serversStore.getMetrics({serverId: props.serverId, scheduleInterval: 'minute'})
  initChart()
})

onBeforeUnmount(() => {
  if (chartInstanceDisc) chartInstanceDisc.destroy()
  if (chartInstanceMemory) chartInstanceMemory.destroy()
})

// обновление данных
const notificationsStore = useNotificationsStore()

// обновление метрики
const serverMetricLog = computed(() => notificationsStore.debug['server_metrics'])
notificationsStore.onDebug('server_metrics')
watch(() => serverMetricLog.value, async () => {
  if(serverMetricLog.value.data.serverId === props.serverId) {
    await serversStore.getMetrics({
      serverId: props.serverId,
      scheduleInterval: 'minute'
    })
    updateChart()
  }
})

</script>
